/**
 * Reflexion im Abendjournal (Roadmap V-10, eigene Fragen F-27).
 *
 * Fuenf Standardfragen; die Abgrenzung der letzten drei steht als Hinweis
 * direkt am Feld, damit sie sich nicht doppeln. Eigene Fragen kommen dazu,
 * alle lassen sich umbenennen, umsortieren und ausblenden. Fragen mit
 * "Mitnahme" (standardmaessig Erkenntnis, Optimierung und Vorsatz) wandern
 * am naechsten Morgen nach oben ins Morgenjournal.
 */
import type { JournalEntry, LocalDate, Reflexion, ReflexionsFrageDef } from '@/domain/types';
import { werteBereinigt } from '@/domain/journal/felder';

export type ReflexionsFrage = 'gut' | 'schlecht' | 'erkenntnis' | 'optimierung' | 'morgen';

export const REFLEXION_FRAGEN: { key: ReflexionsFrage; frage: string; hint: string }[] = [
  { key: 'gut', frage: 'Was lief heute gut?', hint: 'Wo war ich stark?' },
  { key: 'schlecht', frage: 'Was lief heute nicht gut?', hint: 'Wo war ich schwach?' },
  { key: 'erkenntnis', frage: 'Erkenntnis', hint: 'Was habe ich heute gelernt oder verstanden – über mich, andere oder eine Sache?' },
  { key: 'optimierung', frage: 'Optimierung', hint: 'Was verbessere ich grundsätzlich – einen Ablauf, eine Gewohnheit, meine Planung?' },
  { key: 'morgen', frage: 'Was mache ich morgen besser?', hint: 'Ein konkreter, sofort umsetzbarer Vorsatz.' },
];

/** Die drei, die standardmaessig in den naechsten Morgen mitgehen. */
export const MITNAHME: ReflexionsFrage[] = ['erkenntnis', 'optimierung', 'morgen'];

const STANDARD = new Set<string>(REFLEXION_FRAGEN.map((f) => f.key));
const istStandard = (id: string): id is ReflexionsFrage => STANDARD.has(id);

/** Wo Fragen gestellt werden (F-27, erweitert; F-32 auch im freien Journal). */
export type FragenOrt = 'morgen' | 'abend' | 'frei' | 'woche' | 'monat';

export const FRAGEN_ORTE: { ort: FragenOrt; label: string }[] = [
  { ort: 'morgen', label: 'Morgenjournal' },
  { ort: 'abend', label: 'Abendjournal' },
  { ort: 'frei', label: 'Freies Journal' },
  { ort: 'woche', label: 'Wochenreflexion' },
  { ort: 'monat', label: 'Monatsreflexion' },
];

/** Standardfragen der Reflexionen - die Schluessel sind die der bisherigen Antworten. */
export const WOCHEN_FRAGEN: { schluessel: string; frage: string }[] = [
  { schluessel: 'gelaufen', frage: 'Was lief diese Woche gut?' },
  { schluessel: 'gehakt', frage: 'Woran hat es gehakt?' },
  { schluessel: 'gelernt', frage: 'Was habe ich gelernt?' },
  { schluessel: 'naechste_woche', frage: 'Was nehme ich mir für nächste Woche vor?' },
];
export const MONATS_FRAGEN: { schluessel: string; frage: string }[] = [
  { schluessel: 'gut', frage: 'Was lief diesen Monat gut?' },
  { schluessel: 'nicht_gut', frage: 'Was lief nicht gut?' },
  { schluessel: 'erkenntnisse', frage: 'Wichtigste Erkenntnisse' },
  { schluessel: 'optimieren', frage: 'Was optimiere ich?' },
  { schluessel: 'fokus', frage: 'Worauf lege ich im nächsten Monat den Fokus?' },
];

/** Urspruenglicher Text jeder Standardfrage - fuer Antworten von vor F-27. */
const ORIGINAL: Record<string, string> = Object.fromEntries([
  ...REFLEXION_FRAGEN.map((f) => [f.key, f.frage]),
  ...WOCHEN_FRAGEN.map((f) => [`woche:${f.schluessel}`, f.frage]),
  ...MONATS_FRAGEN.map((f) => [`monat:${f.schluessel}`, f.frage]),
]);

export const ortVon = (f: Pick<ReflexionsFrageDef, 'ort'>): FragenOrt => f.ort ?? 'abend';
export const schluesselVon = (f: Pick<ReflexionsFrageDef, 'id' | 'schluessel'>): string => f.schluessel ?? f.id;

/** Alle Standardfragen als verwaltbare Fragen (F-27): Abend, Woche, Monat. Das Morgenjournal hat nur eigene. */
export function standardFragen(ts: string): ReflexionsFrageDef[] {
  const basis = { hidden: false, standard: true, createdAt: ts, updatedAt: ts };
  return [
    ...REFLEXION_FRAGEN.map((f, i) => ({ ...basis, id: f.key, ort: 'abend' as const, frage: f.frage, hint: f.hint, order: i, mitnahme: MITNAHME.includes(f.key) })),
    ...WOCHEN_FRAGEN.map((f, i) => ({ ...basis, id: `woche:${f.schluessel}`, ort: 'woche' as const, schluessel: f.schluessel, frage: f.frage, order: i, mitnahme: false })),
    ...MONATS_FRAGEN.map((f, i) => ({ ...basis, id: `monat:${f.schluessel}`, ort: 'monat' as const, schluessel: f.schluessel, frage: f.frage, order: i, mitnahme: false })),
  ];
}

/** Fragen eines Orts in Reihenfolge. */
export function fragenAm(fragen: ReflexionsFrageDef[], ort: FragenOrt): ReflexionsFrageDef[] {
  return fragen.filter((f) => ortVon(f) === ort).sort((a, b) => a.order - b.order);
}

/**
 * Fragen einer Wochen- oder Monatsreflexion: sichtbare plus ausgeblendete
 * mit Antwort, beschriftet wie beim Beantworten. `answers`/`wortlaut` sind
 * der GESPEICHERTE Stand.
 */
export function reviewFragen(
  fragen: ReflexionsFrageDef[], ort: 'woche' | 'monat', answers: Record<string, string>, wortlaut?: Record<string, string>, werte?: Record<string, unknown>,
): { key: string; label: string; hint?: string; hidden: boolean; feld: ReflexionsFrageDef }[] {
  return fragenAm(fragen, ort)
    .filter((f) => !f.hidden || !!answers[schluesselVon(f)]?.trim() || werte?.[schluesselVon(f)] !== undefined)
    .map((f) => {
      const key = schluesselVon(f);
      const beantwortet = answers[key]?.trim() || werte?.[key] !== undefined;
      const label = wortlaut?.[key] ?? (beantwortet && ORIGINAL[f.id] ? ORIGINAL[f.id]! : f.frage);
      return { key, label, ...(f.hint ? { hint: f.hint } : {}), hidden: f.hidden, feld: f };
    });
}

/**
 * Wortlaut beim Speichern einer Reflexion: Schon Beantwortetes behaelt
 * seinen (Antworten von vor F-27: den urspruenglichen Standardtext), neu
 * Beantwortetes bekommt den aktuellen.
 */
export function reviewWortlaut(
  answers: Record<string, string>, vorher: { answers?: Record<string, string>; wortlaut?: Record<string, string> } | undefined,
  fragen: ReflexionsFrageDef[], ort: 'woche' | 'monat',
): Record<string, string> | undefined {
  const defs = fragenAm(fragen, ort);
  const out: Record<string, string> = {};
  for (const [key, text] of Object.entries(answers)) {
    if (!text.trim()) continue;
    const def = defs.find((f) => schluesselVon(f) === key);
    const alt = vorher?.wortlaut?.[key] ?? (vorher?.answers?.[key]?.trim() && def && ORIGINAL[def.id] ? ORIGINAL[def.id] : undefined);
    const w = alt ?? def?.frage;
    if (w) out[key] = w;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Antwort auf eine Frage - Standardfragen liegen direkt in der Reflexion. */
export function antwort(r: Reflexion | undefined, id: string): string | undefined {
  if (!r) return undefined;
  return istStandard(id) ? r[id] : r.eigene?.[id];
}

/** Setzt eine Antwort (unbereinigt, fuer den Entwurf). */
export function mitAntwort(r: Reflexion | undefined, id: string, text: string): Reflexion {
  return istStandard(id) ? { ...r, [id]: text } : { ...r, eigene: { ...r?.eigene, [id]: text } };
}

/**
 * Die Frage, wie sie beim Beantworten hiess. Eintraege von vor F-27 haben
 * keinen gespeicherten Wortlaut - dort galt noch der urspruengliche Text.
 * `r` ist der GESPEICHERTE Stand, nicht der Entwurf: Was gerade erst getippt
 * wird, steht unter der aktuellen Frage.
 */
export function frageText(r: Reflexion | undefined, f: Pick<ReflexionsFrageDef, 'id' | 'frage'>): string {
  const damals = r?.wortlaut?.[f.id];
  if (damals) return damals;
  if (r && antwort(r, f.id) && ORIGINAL[f.id]) return ORIGINAL[f.id]!;
  return f.frage;
}

/**
 * Wortlaut aller beantworteten Fragen eines GESPEICHERTEN Eintrags - bei
 * Eintraegen von vor F-27 der urspruengliche Text der Standardfragen.
 */
export function wortlautVon(r: Reflexion | undefined): Record<string, string> {
  if (!r) return {};
  const out: Record<string, string> = {};
  for (const id of [...REFLEXION_FRAGEN.map((f) => f.key), ...Object.keys(r.eigene ?? {}), ...Object.keys(r.werte ?? {})]) {
    if (!antwort(r, id) && r.werte?.[id] === undefined) continue;
    const w = r.wortlaut?.[id] ?? (istStandard(id) ? REFLEXION_FRAGEN.find((f) => f.key === id)!.frage : undefined);
    if (w) out[id] = w;
  }
  return out;
}

/** Fragen fuer einen Journaleintrag: sichtbare plus ausgeblendete mit Antwort. */
export function fragenFuer(fragen: ReflexionsFrageDef[], r: Reflexion | undefined, ort: 'morgen' | 'abend' | 'frei' = 'abend'): ReflexionsFrageDef[] {
  return fragenAm(fragen, ort).filter((f) => !f.hidden || !!antwort(r, f.id)?.trim() || r?.werte?.[f.id] !== undefined);
}

/** Was am naechsten Morgen oben steht: beantwortete Fragen mit Mitnahme. */
export function mitnahmePunkte(r: Reflexion | undefined, fragen: ReflexionsFrageDef[]): { id: string; frage: string; text: string }[] {
  if (!r) return [];
  const defs = fragenAm(fragen.length > 0 ? fragen : standardFragen(''), 'abend');
  return defs
    .filter((f) => f.mitnahme && antwort(r, f.id)?.trim())
    .map((f) => ({ id: f.id, frage: frageText(r, f), text: antwort(r, f.id)! }));
}

/**
 * Leere Antworten weg; ohne Inhalt `undefined`. Fuer jede Antwort wird der
 * Wortlaut der Frage festgehalten - ein schon gespeicherter bleibt, sonst
 * gilt der aktuelle aus `fragen`.
 */
export function reflexionBereinigt(r: Reflexion | undefined, fragen: ReflexionsFrageDef[] = []): Reflexion | undefined {
  if (!r) return undefined;
  const out: Reflexion = {};
  const wortlaut: Record<string, string> = {};
  const merke = (id: string) => {
    const w = r.wortlaut?.[id] ?? fragen.find((f) => f.id === id)?.frage;
    if (w) wortlaut[id] = w;
  };
  for (const { key } of REFLEXION_FRAGEN) {
    const t = r[key]?.trim();
    if (t) { out[key] = t; merke(key); }
  }
  const eigene = Object.fromEntries(Object.entries(r.eigene ?? {}).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v));
  if (Object.keys(eigene).length > 0) {
    out.eigene = eigene;
    for (const id of Object.keys(eigene)) merke(id);
  }
  // Werte typisierter Felder (F-32): Zahl, Ja/Nein, Uhrzeit, Checkliste.
  const werte = werteBereinigt(r.werte, fragen);
  if (werte) {
    out.werte = werte;
    for (const id of Object.keys(werte)) merke(id);
  }
  if (r.morgenAufgabeId && out.morgen) out.morgenAufgabeId = r.morgenAufgabeId;
  if (Object.keys(out).length === 0) return undefined;
  if (Object.keys(wortlaut).length > 0) out.wortlaut = wortlaut;
  return out;
}

/** Alle Antworttexte einer Reflexion, dazu die eigenen Fragen - fuer die Suche. */
export function reflexionsTexte(r: Reflexion | undefined): string[] {
  if (!r) return [];
  const eigene = Object.entries(r.eigene ?? {}).flatMap(([id, text]) => [r.wortlaut?.[id], text]);
  // Uhrzeiten und Checklisten sind auch Text; Zahlen und Ja/Nein nicht (F-32).
  const werte = Object.entries(r.werte ?? {}).flatMap(([id, v]) => (typeof v === 'string' || Array.isArray(v) ? [r.wortlaut?.[id], Array.isArray(v) ? v.join(', ') : v] : []));
  return [r.gut, r.schlecht, r.erkenntnis, r.optimierung, r.morgen, ...eigene, ...werte].filter((t): t is string => !!t);
}

export interface Fundstueck {
  date: LocalDate;
  art: 'erkenntnis' | 'optimierung' | 'morgen';
  text: string;
}

/**
 * Sammlung aller Erkenntnisse, Optimierungen und Vorsaetze (Vorschlag):
 * neueste zuerst, optional nach Art und Suchbegriff gefiltert.
 */
export function sammlung(
  eintraege: JournalEntry[],
  filter: { arten?: Fundstueck['art'][]; suche?: string } = {},
): Fundstueck[] {
  const arten = filter.arten ?? ['erkenntnis', 'optimierung', 'morgen'];
  const begriff = filter.suche?.trim().toLowerCase() ?? '';
  const out: Fundstueck[] = [];
  for (const e of eintraege) {
    if (e.type !== 'abend' || !e.reflexion) continue;
    for (const art of arten) {
      const text = e.reflexion[art];
      if (text && (!begriff || text.toLowerCase().includes(begriff))) out.push({ date: e.date, art, text });
    }
  }
  return out.sort((a, b) => b.date.localeCompare(a.date) || arten.indexOf(a.art) - arten.indexOf(b.art));
}
